import { useState } from "react";
import {
  Ticket,
  CaretRight,
  CaretLeft,
  Check,
  CurrencyCircleDollar,
  Receipt,
} from "@phosphor-icons/react";
import ModalCloseButton from "../../ui/ModalCloseButton";
import DatePicker from "../../ui/DatePicker";
import "./PunchCardModals.css";

const PAYMENT_METHOD_OPTIONS = [
  { value: "cash", label: "מזומן" },
  { value: "credit", label: "אשראי" },
  { value: "transfer", label: "העברה" },
  { value: "check", label: "צ׳ק" },
  { value: "bit", label: "אפליקציה (ביט/פייבוקס)" },
  { value: "other", label: "אחר" },
];

const STEP_LABELS = ["פרטים", "תשלום", "הכנסה", "סיכום"];

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(d.getDate()).padStart(2, "0")}`;
};

/**
 * Punch card creation wizard:
 * 1. Card details (price per visit, total visits, dates, notes)
 * 2. Was the card paid upfront?
 * 3. (If paid upfront) record the full amount as income now?
 * 4. Summary + confirm
 */
function PunchCardWizardModal({ onClose, onCreate, isCreating }) {
  const [step, setStep] = useState(1);
  const [pricePerVisit, setPricePerVisit] = useState("");
  const [totalVisits, setTotalVisits] = useState("");
  const [purchaseDate, setPurchaseDate] = useState(todayIso());
  const [hasExpiry, setHasExpiry] = useState(false);
  const [expiryDate, setExpiryDate] = useState("");
  const [notes, setNotes] = useState("");
  const [paidUpfront, setPaidUpfront] = useState(null); // null until answered
  const [recordIncome, setRecordIncome] = useState(null);
  const [purchasePaymentMethod, setPurchasePaymentMethod] = useState("cash");
  const [validationError, setValidationError] = useState("");

  const price = Number(pricePerVisit);
  const visits = Number(totalVisits);
  const totalAmount =
    Number.isFinite(price) && Number.isFinite(visits) ? price * visits : 0;

  const validateDetails = () => {
    if (!Number.isFinite(price) || price <= 0) {
      setValidationError("יש להזין מחיר תקין למפגש");
      return false;
    }
    if (!Number.isInteger(visits) || visits <= 0) {
      setValidationError("יש להזין מספר מפגשים תקין");
      return false;
    }
    if (hasExpiry && !expiryDate) {
      setValidationError("יש לבחור תאריך תפוגה או לבטל את הסימון");
      return false;
    }
    setValidationError("");
    return true;
  };

  const handleSubmit = () => {
    onCreate({
      cardData: {
        pricePerVisit: price,
        totalVisits: visits,
        purchaseDate,
        expiryDate: hasExpiry && expiryDate ? expiryDate : null,
        notes: notes.trim() || null,
        paidUpfront: paidUpfront === true,
        purchasePaymentMethod:
          paidUpfront === true && recordIncome === true
            ? purchasePaymentMethod
            : null,
      },
      recordPurchaseIncome: paidUpfront === true && recordIncome === true,
    });
  };

  return (
    <div className="punch-modal-overlay" onClick={onClose}>
      <div className="punch-modal" onClick={(e) => e.stopPropagation()}>
        <div className="punch-modal-header">
          <div className="punch-modal-title">
            <div className="punch-modal-title-icon">
              <Ticket size={20} weight="fill" />
            </div>
            <div className="punch-modal-title-text">
              <span className="punch-modal-eyebrow">כרטיסיית מפגשים</span>
              <h3>יצירת כרטיסיה חדשה</h3>
            </div>
          </div>
          <ModalCloseButton onClick={onClose} />
        </div>

        <div className="punch-step-indicator">
          {STEP_LABELS.map((label, idx) => {
            const num = idx + 1;
            const isSkipped = num === 3 && paidUpfront === false;
            const state = isSkipped
              ? "skipped"
              : num === step
              ? "current"
              : num < step
              ? "done"
              : "";
            return (
              <div key={label} className={`punch-step ${state}`}>
                {idx > 0 && (
                  <span
                    className={`punch-step-line ${num <= step ? "filled" : ""}`}
                  />
                )}
                <span className="punch-step-dot">
                  {state === "done" ? (
                    <Check size={11} weight="bold" />
                  ) : (
                    num
                  )}
                </span>
                <span className="punch-step-label">{label}</span>
              </div>
            );
          })}
        </div>

        <div className="punch-modal-body">
          {step === 1 && (
            <div className="punch-step-panel" key="step-1">
              <div className="punch-form-row">
                <div className="punch-form-group">
                  <label>מחיר למפגש (₪)</label>
                  <input
                    type="number"
                    min="0"
                    value={pricePerVisit}
                    onChange={(e) => setPricePerVisit(e.target.value)}
                    placeholder="לדוגמה: 200"
                  />
                </div>
                <div className="punch-form-group">
                  <label>מספר מפגשים</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={totalVisits}
                    onChange={(e) => setTotalVisits(e.target.value)}
                    placeholder="לדוגמה: 10"
                  />
                </div>
              </div>

              {totalAmount > 0 && (
                <div className="punch-total-hint">
                  <span className="punch-total-hint-label">
                    שווי הכרטיסיה
                  </span>
                  <span className="punch-total-hint-value">
                    ₪{totalAmount.toLocaleString("he-IL")}
                  </span>
                  <span className="punch-total-hint-breakdown">
                    {visits} מפגשים × ₪{price.toLocaleString("he-IL")}
                  </span>
                </div>
              )}

              <div className="punch-form-group">
                <label>תאריך רכישה</label>
                <DatePicker
                  value={purchaseDate}
                  onChange={(e) => setPurchaseDate(e.target.value)}
                />
              </div>

              <label className="punch-checkbox-row">
                <input
                  type="checkbox"
                  checked={hasExpiry}
                  onChange={(e) => setHasExpiry(e.target.checked)}
                />
                <span>לכרטיסיה יש תאריך תפוגה</span>
              </label>
              {hasExpiry && (
                <div className="punch-form-group">
                  <label>תוקף עד</label>
                  <DatePicker
                    value={expiryDate}
                    onChange={(e) => setExpiryDate(e.target.value)}
                  />
                </div>
              )}

              <div className="punch-form-group">
                <label>הערות (אופציונלי)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  placeholder="הערות פנימיות על הכרטיסיה"
                />
              </div>

              {validationError && (
                <div className="punch-validation-error">{validationError}</div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="punch-question-step punch-step-panel" key="step-2">
              <div className="punch-question-icon">
                <CurrencyCircleDollar size={32} weight="duotone" />
              </div>
              <h4>האם המטופל שילם מראש על הכרטיסיה?</h4>
              <p className="punch-question-desc">
                אם הכרטיסיה שולמה מראש — פגישות שינוקבו מהכרטיסיה יסומנו
                כמשולמות אוטומטית. אם התשלום נגבה אחרי כל פגישה — הפגישות
                ימשיכו במעקב התשלומים הרגיל.
              </p>
              <div className="punch-question-options">
                <button
                  className={`punch-option-btn ${
                    paidUpfront === true ? "selected" : ""
                  }`}
                  onClick={() => setPaidUpfront(true)}
                >
                  כן, שולם מראש
                </button>
                <button
                  className={`punch-option-btn ${
                    paidUpfront === false ? "selected" : ""
                  }`}
                  onClick={() => {
                    setPaidUpfront(false);
                    setRecordIncome(null);
                  }}
                >
                  לא, תשלום אחרי כל פגישה
                </button>
              </div>
            </div>
          )}

          {step === 3 && paidUpfront === true && (
            <div className="punch-question-step punch-step-panel" key="step-3">
              <div className="punch-question-icon">
                <Receipt size={32} weight="duotone" />
              </div>
              <h4>
                להוסיף רישום הכנסה על מלוא הסכום (₪
                {totalAmount.toLocaleString("he-IL")})?
              </h4>
              <p className="punch-question-desc">
                אם תרשום את ההכנסה עכשיו, פגישות שינוקבו ייווצרו בסכום ₪0 כדי
                שההכנסה לא תיספר פעמיים. אם לא — כל פגישה מנוקבת תירשם כהכנסה
                בסך ₪{price > 0 ? price.toLocaleString("he-IL") : "—"}.
              </p>
              <div className="punch-question-options">
                <button
                  className={`punch-option-btn ${
                    recordIncome === true ? "selected" : ""
                  }`}
                  onClick={() => setRecordIncome(true)}
                >
                  כן, רשום הכנסה עכשיו
                </button>
                <button
                  className={`punch-option-btn ${
                    recordIncome === false ? "selected" : ""
                  }`}
                  onClick={() => setRecordIncome(false)}
                >
                  לא, ללא רישום הכנסה
                </button>
              </div>
              {recordIncome === true && (
                <div className="punch-form-group punch-method-select">
                  <label>אמצעי תשלום</label>
                  <select
                    value={purchasePaymentMethod}
                    onChange={(e) => setPurchasePaymentMethod(e.target.value)}
                  >
                    {PAYMENT_METHOD_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="punch-summary-step punch-step-panel" key="step-4">
              <h4>רגע לפני שיוצרים</h4>
              <ul className="punch-summary-list">
                <li>
                  <span>מספר מפגשים</span>
                  <strong>{visits}</strong>
                </li>
                <li>
                  <span>מחיר למפגש</span>
                  <strong>₪{price.toLocaleString("he-IL")}</strong>
                </li>
                <li className="total">
                  <span>שווי כולל</span>
                  <strong>₪{totalAmount.toLocaleString("he-IL")}</strong>
                </li>
                <li>
                  <span>תשלום</span>
                  <strong>
                    {paidUpfront ? "שולם מראש" : "תשלום אחרי כל פגישה"}
                  </strong>
                </li>
                {paidUpfront && (
                  <li>
                    <span>רישום הכנסה</span>
                    <strong>
                      {recordIncome
                        ? "נרשם עכשיו (פגישות מנוקבות ב-₪0)"
                        : "ללא רישום (הכנסה לפי פגישה מנוקבת)"}
                    </strong>
                  </li>
                )}
                {hasExpiry && expiryDate && (
                  <li>
                    <span>תוקף עד</span>
                    <strong>
                      {expiryDate.split("-").reverse().join("/")}
                    </strong>
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>

        <div className="punch-modal-footer">
          {step > 1 && (
            <button
              className="punch-btn secondary"
              onClick={() => {
                // Skip the income step when payment is per-meeting
                if (step === 4 && paidUpfront === false) setStep(2);
                else setStep(step - 1);
              }}
              disabled={isCreating}
            >
              <CaretRight size={16} />
              חזרה
            </button>
          )}
          {step < 4 ? (
            <button
              className="punch-btn primary"
              disabled={
                (step === 2 && paidUpfront === null) ||
                (step === 3 && recordIncome === null)
              }
              onClick={() => {
                if (step === 1) {
                  if (!validateDetails()) return;
                  setStep(2);
                } else if (step === 2) {
                  setStep(paidUpfront === true ? 3 : 4);
                } else {
                  setStep(4);
                }
              }}
            >
              המשך
              <CaretLeft size={16} />
            </button>
          ) : (
            <button
              className="punch-btn primary"
              onClick={handleSubmit}
              disabled={isCreating}
            >
              <Check size={16} weight="bold" />
              {isCreating ? "יוצר כרטיסיה..." : "צור כרטיסיה"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default PunchCardWizardModal;
